const TENANT_ID_PATTERN = /^[a-z0-9-]+$/;
const LOCALHOST_SUFFIX = ".localhost";
const DEFAULT_ROOT_DOMAIN = "v2.edthos.app";

/** School vanity domains that are not {tenant}.{ROOT_DOMAIN}. */
const DEFAULT_TENANT_HOST_MAP = {
  "onlineportal.lumaxacademy.com.sg": "lumax",
  "onlineportal.edusphere.edu.sg": "edusphere",
};

export const normalizeTenantId = (value) => {
  if (!value || typeof value !== "string") {
    return "";
  }
  return value.trim().toLowerCase();
};

export const isValidTenantId = (tenantId) => {
  return TENANT_ID_PATTERN.test(tenantId);
};

/** Prod base domain, e.g. v2.edthos.app → hosts like edusphere.v2.edthos.app */
export const getRootDomain = () => {
  const fromEnv = (process.env.REACT_APP_ROOT_DOMAIN || "").trim().toLowerCase();
  return fromEnv || DEFAULT_ROOT_DOMAIN;
};

export const getCurrentHostname = () => {
  if (typeof window === "undefined" || !window.location) {
    return "";
  }
  return normalizeTenantId(window.location.hostname);
};

const extractSubdomain = (hostname, suffix) => {
  if (!hostname.endsWith(suffix)) {
    return "";
  }
  const candidate = hostname.slice(0, -suffix.length);
  if (!candidate || candidate.includes(".")) {
    return "";
  }
  return candidate;
};

const isPlainLocalDevelopmentHost = (normalizedHost) => {
  return (
    normalizedHost === "localhost" ||
    normalizedHost === "127.0.0.1" ||
    normalizedHost === "::1"
  );
};

/**
 * Optional JSON map: {"onlineportal.example.com":"lumax"}
 * Merged over DEFAULT_TENANT_HOST_MAP (env wins on conflicts).
 */
export const parseTenantHostMap = (rawMap) => {
  if (!rawMap || typeof rawMap !== "string") {
    return {};
  }

  try {
    const parsed = JSON.parse(rawMap);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return {};
    }

    const normalizedMap = {};
    Object.entries(parsed).forEach(([hostname, tenantId]) => {
      const normalizedHost = normalizeTenantId(hostname);
      const normalizedTenantId = normalizeTenantId(tenantId);
      if (!normalizedHost || !isValidTenantId(normalizedTenantId)) {
        return;
      }
      normalizedMap[normalizedHost] = normalizedTenantId;
    });
    return normalizedMap;
  } catch (error) {
    console.error("[tenantConfig] Invalid REACT_APP_TENANT_HOST_MAP JSON:", error);
    return {};
  }
};

export const getTenantHostMap = () => {
  const fromEnv = parseTenantHostMap(process.env.REACT_APP_TENANT_HOST_MAP || "");
  return { ...DEFAULT_TENANT_HOST_MAP, ...fromEnv };
};

/**
 * Host → tenant slug (same idea as tms-sozcode ROOT_DOMAIN).
 * - edusphere.localhost → edusphere
 * - edusphere.v2.edthos.app when REACT_APP_ROOT_DOMAIN=v2.edthos.app
 * - onlineportal.lumaxacademy.com.sg → lumax (custom host map)
 */
export const resolveTenantIdFromHostname = (hostname, rootDomainInput) => {
  const normalizedHost = normalizeTenantId(hostname);
  if (!normalizedHost) {
    return "";
  }

  if (isPlainLocalDevelopmentHost(normalizedHost)) {
    const previewTenant = normalizeTenantId(process.env.REACT_APP_PREVIEW_TENANT_ID || "");
    if (isValidTenantId(previewTenant)) {
      return previewTenant;
    }
    return "";
  }

  const localhostSubdomain = extractSubdomain(normalizedHost, LOCALHOST_SUFFIX);
  if (localhostSubdomain && isValidTenantId(localhostSubdomain)) {
    return localhostSubdomain;
  }

  const rootDomain = normalizeTenantId(rootDomainInput || getRootDomain());
  if (rootDomain) {
    if (normalizedHost === rootDomain || normalizedHost === `www.${rootDomain}`) {
      return "";
    }

    const productionSubdomain = extractSubdomain(normalizedHost, `.${rootDomain}`);
    if (productionSubdomain && isValidTenantId(productionSubdomain)) {
      return productionSubdomain;
    }
  }

  const hostMappedTenant = getTenantHostMap()[normalizedHost];
  if (hostMappedTenant && isValidTenantId(hostMappedTenant)) {
    return hostMappedTenant;
  }

  // Vercel preview hosts have no school subdomain; use the same preview tenant as local dev.
  if (normalizedHost.endsWith(".vercel.app")) {
    const previewTenant = normalizeTenantId(process.env.REACT_APP_PREVIEW_TENANT_ID || "");
    if (isValidTenantId(previewTenant)) {
      return previewTenant;
    }
  }

  return "";
};

export const getTenantContext = () => {
  const hostname = getCurrentHostname();
  const tenantId = resolveTenantIdFromHostname(hostname);
  return { hostname, tenantId };
};

export const parseTenantMap = (rawMap) => {
  if (!rawMap || typeof rawMap !== "string") {
    return {};
  }

  try {
    const parsed = JSON.parse(rawMap);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return {};
    }

    const normalizedMap = {};
    Object.entries(parsed).forEach(([tenantId, config]) => {
      const normalizedTenantId = normalizeTenantId(tenantId);
      if (!isValidTenantId(normalizedTenantId)) {
        return;
      }
      if (!config || typeof config !== "object") {
        return;
      }

      const url = typeof config.url === "string" ? config.url.trim() : "";
      const anonKey = typeof config.anonKey === "string" ? config.anonKey.trim() : "";
      if (!url || !anonKey) {
        return;
      }

      const entry = { url, anonKey };
      if (config.disabled === true) {
        entry.disabled = true;
      }
      normalizedMap[normalizedTenantId] = entry;
    });

    return normalizedMap;
  } catch (error) {
    console.error("[tenantConfig] Invalid REACT_APP_TENANT_SUPABASE_MAP JSON:", error);
    return {};
  }
};

export const getEnvTenantMap = () => {
  return parseTenantMap(process.env.REACT_APP_TENANT_SUPABASE_MAP || "");
};

export const getLocalTenantConfig = (tenantId) => {
  const normalizedTenantId = normalizeTenantId(tenantId);
  if (!isValidTenantId(normalizedTenantId)) {
    return null;
  }

  const map = getEnvTenantMap();
  const config = map[normalizedTenantId];
  if (!config || config.disabled) {
    return null;
  }

  return { url: config.url, anonKey: config.anonKey };
};

export const shouldUseRuntimeTenantLookup = () => {
  return process.env.REACT_APP_ENABLE_RUNTIME_TENANT_LOOKUP === "true";
};

export const getTenantConfigEndpoint = () => {
  return process.env.REACT_APP_TENANT_CONFIG_ENDPOINT || "/api/tenant-config";
};
